import { useId, useMemo, useState } from 'react';
import { Alert, Switch, Text } from '@backstage/ui';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import {
  configurationBounds,
  hiddenFieldIds,
  sizedReservation,
  type FieldValues,
} from '@internal/plugin-waadoo-config-common';
import {
  ConfigurationRow,
  ConfigurationSidePanel,
  ConfigurationWorkbench,
  CUSTOM_INFO,
  CUSTOM_PROFILE,
  DATABASE_MODE_KEYS,
  DatabaseSection,
  descriptorOf,
  formatCpu,
  formatMemory,
  ProfileCards,
  projectEnvironmentsTranslationRef,
  SectionCard,
  useConfigurationApply,
  useConfigurationReference,
  useConfigurationRows,
  waadooIcons,
  WorkbenchAside,
  WorkbenchMain,
  type ConfigurationEditorInput,
  type ConfigurationResponse,
  type ConfigurationTabProps,
  type ProfileCardOption,
} from '@internal/plugin-waadoo-core';
import { odooTranslationRef } from '../odoo/translation';
import { cacheFieldNaming } from '../odoo/valkeyRenaming';
import {
  applyProfile,
  CONFIGURATION_FIELDS,
  DATABASE_FIELDS,
  lockedFields,
  peakReservation,
  planDraft,
  profileOf,
  type ConfigurationField,
  type ConfigurationProblem,
  type DatabaseMode,
  type EnvironmentConfiguration,
} from './configuration';

const HighAvailabilityIcon = waadooIcons.highAvailability;

/** The label of each field, in the panel and the problems. */
const FIELD_KEYS = {
  workers: 'configuration.field.workers',
  odooCpuRequest: 'configuration.field.odooCpuRequest',
  odooCpuLimit: 'configuration.field.odooCpuLimit',
  odooMemoryRequest: 'configuration.field.odooMemoryRequest',
  odooMemoryLimit: 'configuration.field.odooMemoryLimit',
  filestoreSize: 'configuration.field.filestoreSize',
  dbCpuRequest: 'configuration.field.dbCpuRequest',
  dbCpuLimit: 'configuration.field.dbCpuLimit',
  dbMemoryRequest: 'configuration.field.dbMemoryRequest',
  dbMemoryLimit: 'configuration.field.dbMemoryLimit',
  databaseSize: 'configuration.field.databaseSize',
  haCache: 'configuration.field.haCache',
  haAutoscaling: 'configuration.field.haAutoscaling',
  haMinReplicas: 'configuration.field.haMinReplicas',
  haMaxReplicas: 'configuration.field.haMaxReplicas',
  haTargetCpu: 'configuration.field.haTargetCpu',
  haScaleDownWindow: 'configuration.field.haScaleDownWindow',
  haDbMode: 'configuration.field.haDbMode',
  haDbSync: 'configuration.field.haDbSync',
} as const satisfies Record<ConfigurationField, string>;

/** The locks said in the core's words; `ephemeral` is said in Odoo's. */
const LOCK_KEYS = {
  'read-only': 'configuration.lockReadOnly',
  production: 'configuration.lockProduction',
} as const;

/** The unit each number is typed in, as the creation form shows them. */
const UNITS: Partial<Record<ConfigurationField, string>> = {
  odooCpuRequest: 'm',
  odooCpuLimit: 'm',
  odooMemoryRequest: 'Mio',
  odooMemoryLimit: 'Mio',
  filestoreSize: 'Gio',
  dbCpuRequest: 'm',
  dbCpuLimit: 'm',
  dbMemoryRequest: 'Mio',
  dbMemoryLimit: 'Mio',
  databaseSize: 'Gio',
  haTargetCpu: '%',
  haScaleDownWindow: 's',
};

/**
 * The fields of the « Haute disponibilité » section, folded unless one of
 * them is changed or differs from the reference. The other HA fields sit
 * with what they size: the pods and Valkey in « Odoo », the instances and
 * the replication in « PostgreSQL et volumes ».
 */
const HA_SECTION_FIELDS: readonly ConfigurationField[] = [
  'haAutoscaling',
  'haTargetCpu',
  'haScaleDownWindow',
];

/**
 * Whether « Haute disponibilité » is unfolded: always while one of its
 * fields has a problem — folded, Apply would stay disabled for a reason the
 * reader could not see —, else as the reader last toggled it, else open when
 * one of its fields is changed or differs from the reference (`byDefault`).
 *
 * Defensive today: the number inputs (react-aria NumberField, `NumberInput`)
 * clamp and snap to the caller's bounds, so the draft cannot carry a bounds
 * problem on these fields from the UI.
 */
export function haSectionOpen(input: {
  problems: boolean;
  toggled: boolean | undefined;
  byDefault: boolean;
}): boolean {
  return input.problems || (input.toggled ?? input.byDefault);
}

const NUMBER = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });

/**
 * The « Configuration » tab of a branch page (mockup « D », 2026-10-16;
 * layout « U1 », 2026-10-16, after « C3 »): three columns (`Workbench`,
 * components/waadooForm, as the creation form) — the project's environments,
 * each opening its own tab; the environment's settings as its `values.yaml`
 * holds them, in cards (« Capacité » and its profiles, « Odoo »,
 * « PostgreSQL et volumes », « Haute disponibilité » folded), each field
 * beside the value of a reference environment (« Comparer avec »); and a
 * panel with the live quota of its namespace, the comparison, the changes
 * and the actions. Editable as the caller's level allows
 * (`configuration.ts`) — every field for the admin tier, within the creation
 * form's bounds for an editor (a field already beyond them locked, « Réservé
 * aux administrateurs »), nothing for a plain member, for an editor on prod,
 * nor on an ephemeral branch, whose EFFECTIVE configuration is shown.
 * « Appliquer » launches `env-configure` with the fields changed, then
 * the caller is taken to the task's page (`onLaunched`).
 *
 * The Odoo module's contribution (`branch.ConfigurationTab`, genericity
 * plan step 4, 2026-10-24): moved from the core unchanged, built on its
 * shared pieces — the frame (`ConfigurationWorkbench`), the rows
 * (`useConfigurationRows`), the PostgreSQL section (`DatabaseSection`) and
 * the side panel (`ConfigurationSidePanel`, `useConfigurationApply`,
 * `useConfigurationReference`, shared with the generic tab since step 5).
 */
export function ConfigurationTab(props: ConfigurationTabProps) {
  const { project, branch, templateRef, reloadSignal, onLaunched } = props;
  return (
    <ConfigurationWorkbench
      project={project}
      branch={branch}
      reloadSignal={reloadSignal}
      renderEditor={({
        answer,
        current,
        permanentNames,
      }: ConfigurationEditorInput<EnvironmentConfiguration>) => (
        <ConfigurationEditor
          project={project}
          branch={branch}
          templateRef={templateRef}
          answer={answer}
          current={current}
          permanentNames={permanentNames}
          reloadSignal={reloadSignal}
          onLaunched={onLaunched}
        />
      )}
    />
  );
}

function ConfigurationEditor(props: {
  project: string;
  branch: string;
  templateRef: string | undefined;
  answer: ConfigurationResponse<EnvironmentConfiguration>;
  current: EnvironmentConfiguration;
  permanentNames: string[];
  reloadSignal?: number;
  onLaunched: (taskId: string) => void;
}) {
  const {
    project,
    branch,
    templateRef,
    answer,
    current: answered,
    permanentNames,
    onLaunched,
  } = props;
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const { t: tOdoo } = useTranslationRef(odooTranslationRef);
  const idPrefix = useId();
  // Compiled once by `fetchConfiguration` (`descriptorOf` caches it); under
  // the module's field names whatever the catalog's (valkeyRenaming.ts,
  // transitional): the route's values in, the fields sent out, translated.
  const naming = useMemo(
    () => cacheFieldNaming(descriptorOf(answer.descriptor)),
    [answer.descriptor],
  );
  const { descriptor } = naming;
  const current = useMemo(() => naming.toModel(answered), [naming, answered]);
  const [draft, setDraft] = useState<EnvironmentConfiguration>(current);
  // Undefined: folded or open as `haOpenByDefault` says; set once toggled.
  const [haToggled, setHaToggled] = useState<boolean>();

  const editable = answer.editable;
  const grant = answer.access === 'admin' ? 'admin' : 'editor';
  const bounds = useMemo(
    () => configurationBounds(descriptor, grant),
    [descriptor, grant],
  );
  const locked = useMemo(
    () => lockedFields(descriptor, current, answer.access),
    [descriptor, current, answer.access],
  );
  // The fields meaningless for this environment (their `when`, over its
  // STORED values, the non-configurable ones included): the filestore's size
  // with the attachments on S3 (`attachmentsS3`, chosen at the creation).
  // Not shown, never set by a profile, so never sent: its value stays.
  const hidden = useMemo(() => {
    const ids = hiddenFieldIds(descriptor, { ...answer.inputs, ...current });
    return new Set(CONFIGURATION_FIELDS.filter(field => ids.has(field)));
  }, [descriptor, answer.inputs, current]);
  const frozen = useMemo(
    () => new Set<ConfigurationField>([...locked, ...hidden]),
    [locked, hidden],
  );
  const plan = useMemo(
    () => planDraft({ descriptor, current, draft, grant }),
    [descriptor, current, draft, grant],
  );
  const applying = useConfigurationApply({
    project,
    branch,
    templateRef,
    onLaunched,
  });
  const { pending } = applying;
  const sleeping = answer.kind === 'permanent' && !answer.active;

  const compare = useConfigurationReference<EnvironmentConfiguration>({
    project,
    branch,
    permanentNames,
    reloadSignal: props.reloadSignal,
  });
  const { referenceEnvironment, referenceLabel } = compare;
  const reference = useMemo(
    () => naming.toModel(compare.reference),
    [naming, compare.reference],
  );

  const isDisabled = (field: ConfigurationField) =>
    !editable || pending || locked.has(field);
  const set = <F extends ConfigurationField>(
    field: F,
    value: EnvironmentConfiguration[F] | undefined,
  ) => {
    if (value === undefined) return;
    setDraft(previous => ({ ...previous, [field]: value }));
  };

  const fieldLabel = (field: ConfigurationField) => tOdoo(FIELD_KEYS[field]);
  const valueText = (
    field: ConfigurationField,
    value: EnvironmentConfiguration[ConfigurationField],
  ): string => {
    if (typeof value === 'boolean') {
      return value ? t('configuration.yes') : t('configuration.no');
    }
    if (field === 'haDbMode') {
      return t(DATABASE_MODE_KEYS[value as DatabaseMode]);
    }
    const unit = UNITS[field];
    const text = NUMBER.format(value as number);
    return unit ? `${text}\u00a0${unit}` : text;
  };
  const problemText = (problem: ConfigurationProblem): string => {
    if (problem.key === 'text') {
      // A refusal the tab has no words for: the engine's French.
      return problem.text;
    }
    const field = fieldLabel(problem.field);
    const value = (name: string) => String(problem.values?.[name] ?? '');
    switch (problem.key) {
      case 'locked':
        return t('configuration.problemLocked', { field });
      case 'bounds': {
        const text = t('configuration.problemBounds', {
          field,
          min: value('min'),
          max: value('max'),
          step: value('step'),
        });
        return grant === 'editor'
          ? `${text} ${t('configuration.problemBeyondEditors')}`
          : text;
      }
      case 'workers':
        return tOdoo('configuration.problemWorkers', {
          field,
          allowed: value('allowed'),
        });
      case 'shrink':
        return t('configuration.problemShrink', {
          field,
          current: value('current'),
        });
      case 'requestAboveLimit':
        return t('configuration.problemRequestAboveLimit', { field });
      case 'maxBelowMin':
        return t('configuration.problemMaxBelowMin');
      case 'connections':
        return tOdoo('configuration.problemConnections', {
          floor: value('floor'),
          max: value('max'),
        });
      default:
        return t('configuration.problemSharedBuffers', {
          buffers: value('buffers'),
        });
    }
  };
  const problemsOf = (...fields: ConfigurationField[]) =>
    plan.problems
      .filter(problem => problem.field && fields.includes(problem.field))
      .map(problemText);
  // The refusals the tab has no words for (a rule without `id`): said once,
  // in the panel — never hidden, as they keep « Appliquer » disabled —, and
  // pointed out on the fields their rule's test reads, which are marked
  // invalid and unfold « Haute disponibilité » as a keyed refusal does.
  const unplacedProblems = plan.problems
    .filter(problem => problem.key === 'text')
    .map(problemText);
  const unplacedFields = new Set(
    plan.problems.flatMap(problem =>
      problem.key === 'text' ? problem.fields : [],
    ),
  );
  const invalidField = (field: ConfigurationField) =>
    problemsOf(field).length > 0 || unplacedFields.has(field);
  const rows = useConfigurationRows<EnvironmentConfiguration>({
    idPrefix,
    ranges: bounds.ranges,
    draft,
    current,
    reference,
    referenceEnvironment,
    referenceLabel,
    locked,
    label: fieldLabel,
    unit: field => UNITS[field],
    valueText,
    isDisabled,
    invalid: invalidField,
    problems: problemsOf,
    set: (field, value) =>
      setDraft(previous => ({ ...previous, [field]: value })),
  });
  const {
    ids,
    was,
    hintText,
    referenceText,
    fieldsText,
    number,
    pairRow,
    choice,
    sizeRow,
  } = rows;
  const adminsOnly = rows.lockedLabel;
  const podsText = (config: EnvironmentConfiguration) =>
    config.haAutoscaling
      ? `${config.haMinReplicas} → ${config.haMaxReplicas}`
      : '1';

  const profile = profileOf(descriptor, draft);
  const reservation = (values: FieldValues) => {
    const { cpu, memory } = sizedReservation(descriptor, values);
    return `${formatCpu(cpu)} · ${formatMemory(memory)}`;
  };
  const profileOptions: ProfileCardOption<string>[] = [
    ...descriptor.profiles.map(item => ({
      id: item.id,
      label: item.label,
      // Over the draft: a profile that leaves a sized field out keeps it.
      figures: reservation({ ...draft, ...item.values }),
    })),
    {
      id: CUSTOM_PROFILE,
      label: CUSTOM_INFO.label,
      figures: reservation(draft),
    },
  ];

  const before = peakReservation(descriptor, current, grant);
  const after = peakReservation(descriptor, plan.desired, grant);
  const canApply = applying.canApply({
    editable,
    changes: plan.changes.length,
    problems: plan.problems.length,
  });
  let lockText: string | undefined;
  if (answer.lock === 'ephemeral') {
    lockText = tOdoo('configuration.lockEphemeral');
  } else if (answer.lock && answer.lock !== 'unreadable') {
    lockText = t(LOCK_KEYS[answer.lock]);
  }
  const differs = (field: ConfigurationField) =>
    reference !== undefined && reference[field] !== draft[field];
  const haOpenByDefault = HA_SECTION_FIELDS.some(
    field => draft[field] !== current[field] || differs(field),
  );
  const haProblems = HA_SECTION_FIELDS.some(invalidField);
  const haOpen = haSectionOpen({
    problems: haProblems,
    toggled: haToggled,
    byDefault: haOpenByDefault,
  });
  const differences = CONFIGURATION_FIELDS.filter(
    field => !hidden.has(field) && differs(field),
  ).length;
  const haDifferences = HA_SECTION_FIELDS.filter(differs).length;

  const main = (
    <div className="waadoo-config__main">
      {lockText && (
        <Alert
          status="info"
          icon
          title={lockText}
          className="waadoo-config__banner"
        />
      )}
      {sleeping && (
        <Alert
          status="info"
          icon
          title={t('configuration.sleeping', { branch })}
          className="waadoo-config__banner"
        />
      )}
      <SectionCard
        icon={waadooIcons.resources}
        title={t('configuration.capacityTitle')}
        description={tOdoo('configuration.capacityDescription')}
      >
        <ProfileCards
          accessibleLabel={t('configuration.profile')}
          value={profile}
          options={profileOptions}
          onChange={id => {
            if (id !== CUSTOM_PROFILE) {
              setDraft(previous =>
                applyProfile(descriptor, previous, current, id, frozen),
              );
            }
          }}
          isDisabled={!editable || pending}
          compact
        />
      </SectionCard>

      <SectionCard
        icon={waadooIcons.processes}
        title={tOdoo('configuration.odooSection')}
        titleId={ids('odoo')}
      >
        <ConfigurationRow
          label={fieldLabel('workers')}
          labelId={ids('workers')}
          locked={locked.has('workers')}
          lockedLabel={adminsOnly}
          hint={hintText(was('workers'), tOdoo('configuration.workersHint'))}
          reference={referenceText(fieldsText('workers'))}
          problems={problemsOf('workers')}
        >
          {choice({
            label: fieldLabel('workers'),
            selected: String(draft.workers),
            items: (bounds.values.workers ?? []).map(value => ({
              id: String(value),
              label: String(value),
            })),
            disabled: isDisabled('workers'),
            onSelect: id => set('workers', Number(id)),
          })}
        </ConfigurationRow>
        {pairRow(
          'odooCpu',
          tOdoo('configuration.odooCpu'),
          'odooCpuRequest',
          'odooCpuLimit',
          t('configuration.perPod'),
        )}
        {pairRow(
          'odooMemory',
          tOdoo('configuration.odooMemory'),
          'odooMemoryRequest',
          'odooMemoryLimit',
          t('configuration.perPod'),
        )}
        <ConfigurationRow
          label={t('configuration.pods')}
          labelId={ids('pods')}
          locked={locked.has('haMinReplicas') || locked.has('haMaxReplicas')}
          lockedLabel={adminsOnly}
          hint={hintText(was('haMinReplicas', 'haMaxReplicas'))}
          reference={referenceText(podsText)}
          problems={problemsOf('haMinReplicas', 'haMaxReplicas')}
        >
          {draft.haAutoscaling ? (
            <>
              {number('haMinReplicas', ids('pods'))}
              <span aria-hidden="true">→</span>
              {number('haMaxReplicas', ids('pods'))}
            </>
          ) : (
            <Text variant="body-small" color="secondary">
              {tOdoo('configuration.singlePod')}
            </Text>
          )}
        </ConfigurationRow>
        <ConfigurationRow
          label={fieldLabel('haCache')}
          labelId={ids('haCache')}
          lockedLabel={adminsOnly}
          hint={
            plan.desired.haAutoscaling
              ? hintText(tOdoo('configuration.cacheForced'))
              : undefined
          }
          reference={referenceText(fieldsText('haCache'))}
        >
          <Switch
            aria-labelledby={ids('haCache')}
            isSelected={plan.desired.haCache}
            isDisabled={isDisabled('haCache') || draft.haAutoscaling}
            onChange={value => set('haCache', value)}
          />
        </ConfigurationRow>
      </SectionCard>

      <DatabaseSection
        title={tOdoo('configuration.databaseSection')}
        titleId={ids('database')}
        rows={rows}
        fields={DATABASE_FIELDS}
        modeOf={config => config.haDbMode}
        synchronous={plan.desired.haDbSync}
      >
        {hidden.has('filestoreSize') ? (
          <ConfigurationRow
            label={tOdoo('configuration.attachments')}
            labelId={ids('attachments')}
            lockedLabel={adminsOnly}
          >
            <Text variant="body-small" color="secondary">
              {tOdoo('configuration.attachmentsS3')}
            </Text>
          </ConfigurationRow>
        ) : (
          sizeRow('filestoreSize', tOdoo('configuration.filestoreHint'))
        )}
      </DatabaseSection>

      <SectionCard
        className="waadoo-config__ha"
        icon={HighAvailabilityIcon}
        titleId={ids('ha')}
        divider={haOpen}
        title={
          <button
            type="button"
            className="waadoo-config__ha-toggle"
            aria-expanded={haOpen}
            aria-disabled={haProblems ? 'true' : undefined}
            aria-controls={ids('ha-body')}
            onClick={() => {
              if (!haProblems) setHaToggled(!haOpen);
            }}
          >
            {t('configuration.ha')}
          </button>
        }
        aside={
          haOpen ? undefined : (
            <Text
              as="span"
              variant="body-small"
              color="secondary"
              className="waadoo-config__ha-summary"
            >
              {[
                HA_SECTION_FIELDS.map(fieldLabel).join(', '),
                haDifferences > 0 && referenceLabel
                  ? t('configuration.differences', {
                      count: haDifferences,
                      branch: referenceLabel,
                    })
                  : '',
              ]
                .filter(Boolean)
                .join(' — ')}
            </Text>
          )
        }
      >
        <div id={ids('ha-body')} hidden={!haOpen}>
          <ConfigurationRow
            label={fieldLabel('haAutoscaling')}
            labelId={ids('haAutoscaling')}
            lockedLabel={adminsOnly}
            hint={hintText(tOdoo('configuration.autoscalingHint'))}
            reference={referenceText(fieldsText('haAutoscaling'))}
          >
            <Switch
              aria-labelledby={ids('haAutoscaling')}
              isSelected={draft.haAutoscaling}
              isDisabled={isDisabled('haAutoscaling')}
              onChange={value => set('haAutoscaling', value)}
            />
          </ConfigurationRow>
          {draft.haAutoscaling &&
            (['haTargetCpu', 'haScaleDownWindow'] as const).map(field => (
              <ConfigurationRow
                key={field}
                label={fieldLabel(field)}
                labelId={ids(field)}
                locked={locked.has(field)}
                lockedLabel={adminsOnly}
                hint={hintText(was(field))}
                reference={referenceText(fieldsText(field))}
                problems={problemsOf(field)}
              >
                {number(field, ids(field))}
              </ConfigurationRow>
            ))}
        </div>
      </SectionCard>
    </div>
  );

  const aside = (
    <ConfigurationSidePanel
      branch={branch}
      answer={answer}
      compare={{
        ...compare,
        read: reference !== undefined,
        differences,
      }}
      choice={choice}
      changes={plan.changes}
      fieldLabel={fieldLabel}
      valueText={valueText}
      problems={unplacedProblems}
      peak={{ before, after }}
      restart={
        plan.desired.haCache
          ? tOdoo('configuration.restartRolling')
          : tOdoo('configuration.restartRecreate')
      }
      apply={applying}
      canApply={canApply}
      onApply={() => void applying.apply(naming.toDescriptor(plan.asked))}
      onCancel={() => setDraft(current)}
    />
  );

  return (
    <>
      <WorkbenchMain>{main}</WorkbenchMain>
      <WorkbenchAside>{aside}</WorkbenchAside>
    </>
  );
}
